// ファイル編集で見せる範囲と、仮想パス → 実際のパスの対応。agent が使う。
// 見せるのは設定・プラグイン・データパックなどだけ。ワールドの本体と Panel が管理するファイルは書き込ませない。
import { z } from 'zod';
import { addonKindsOf } from './addons.js';

import { VirtualPathSchema } from './virtual-path.js';
export { VirtualPathSchema, normalizeVirtualPath } from './virtual-path.js';

export type RootKind = 'live' | 'copied' | 'readonly';

export interface FileRoot {
  /** 仮想パスの先頭 */
  name: string;
  label: string;
  /** /data からの相対パス */
  dataPath: string;
  /**
   * live: bind mount されていて起動中も直接編集できる（反映はプラグインの再読み込みか再起動）
   * copied: 起動時にコピーされる。起動中は runtime 側が正なので、停止中だけ編集できる
   * readonly: 閲覧だけ
   */
  kind: RootKind;
  /** アップロード（jar など）を受け付けるか */
  upload: boolean;
}

export function fileRoots(family: string): FileRoot[] {
  const { plugins: bukkit, mods: modded } = addonKindsOf(family);
  const roots: FileRoot[] = [
    {
      name: 'shared',
      label: 'サーバー設定（server.properties など）',
      dataPath: 'shared',
      kind: 'copied',
      upload: false,
    },
    { name: 'datapacks', label: 'データパック', dataPath: 'worlds/overworld/datapacks', kind: 'live', upload: true },
    { name: 'worlds', label: 'ワールド（閲覧のみ）', dataPath: 'worlds', kind: 'readonly', upload: false },
  ];
  if (bukkit)
    roots.unshift(
      { name: 'plugins', label: 'プラグイン', dataPath: 'family/bukkit/plugins', kind: 'live', upload: true },
      ...(!modded
        ? [
            {
              name: 'config',
              label: 'Paper の設定（config/）',
              dataPath: 'family/bukkit/config',
              kind: 'live' as const,
              upload: false,
            },
          ]
        : []),
      {
        name: 'bukkit',
        label: 'bukkit.yml・spigot.yml など',
        dataPath: 'family/bukkit/files',
        kind: 'copied',
        upload: false,
      },
    );
  if (modded)
    roots.unshift(
      {
        name: 'config',
        label: 'Mod の設定（config/）',
        dataPath: `family/${family}/config`,
        kind: 'live',
        upload: false,
      },
      { name: 'custom-mods', label: '手動で入れる Mod', dataPath: `custom-mods/${family}`, kind: 'live', upload: true },
    );
  return roots;
}

export interface ResolvedPath {
  root: FileRoot;
  /** root の中の相対パス（/ 区切り。root そのものなら ''） */
  relative: string;
}

export function resolveVirtual(family: string, virtualPath: string): ResolvedPath {
  const path = VirtualPathSchema.parse(virtualPath);
  if (/(^|\/)key\.pem$/i.test(path)) throw new Error('Floodgate の鍵は Panel が管理しています');
  const [head, ...rest] = path.split('/');
  const root = fileRoots(family).find((r) => r.name === head);
  if (!root) throw new Error('見せていない場所です');
  return { root, relative: rest.join('/') };
}

/** 編集してよいか。起動中のコピー管理ファイルと閲覧専用は不可 */
export function writable(resolved: ResolvedPath, running: boolean): { ok: boolean; reason?: string } {
  if (resolved.root.kind === 'readonly') return { ok: false, reason: 'この場所は閲覧だけできます' };
  if (resolved.root.kind === 'copied' && running)
    return {
      ok: false,
      reason: 'サーバーの起動中は編集できません（停止時に上書きされるため）。停止してから編集してください',
    };
  if (!resolved.relative) return { ok: false, reason: 'フォルダそのものは編集できません' };
  return { ok: true };
}

/** 表示・保存してはいけない値を伏せる（RCON のパスワードなど。agent が上書きする値でもある） */
export function maskSecrets(fileName: string, content: string): string {
  if (!fileName.endsWith('server.properties')) return content;
  return content.replace(/^(rcon\.password\s*=).*$/gm, '$1********');
}

export const FileEntrySchema = z.object({
  name: z.string(),
  type: z.enum(['file', 'directory']),
  size: z.number().int(),
  modifiedAt: z.string(),
});
export type FileEntry = z.infer<typeof FileEntrySchema>;

export const FileListingSchema = z.object({
  path: z.string(),
  writable: z.boolean(),
  upload: z.boolean(),
  reason: z.string().optional(),
  entries: z.array(FileEntrySchema),
});
export type FileListing = z.infer<typeof FileListingSchema>;

export const FileContentSchema = z.object({
  path: z.string(),
  content: z.string(),
  sha256: z.string(),
  writable: z.boolean(),
  reason: z.string().optional(),
});
export type FileContent = z.infer<typeof FileContentSchema>;

/** 編集できるテキストの最大サイズ */
export const MAX_TEXT_BYTES = 1024 * 1024;
/** アップロードの最大サイズ */
export const MAX_UPLOAD_BYTES = 64 * 1024 * 1024;
/** agent に送る 1 回分（WebSocket の上限 1MB に収める） */
export const UPLOAD_CHUNK_BYTES = 512 * 1024;
